import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    CheckCircle2,
    Clock,
    ExternalLink,
    History,
    Loader2,
    Scale,
    Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { crud } from "../lib/api";
import { tahkimMerkez, TAHKIM_ASAMALARI, ODEME_DURUMLARI } from "../lib/tahkimApi";
import TahkimAsamaGecisModal from "./tahkim/TahkimAsamaGecisModal";
import DosyaDurumuWorkflowPanel from "./DosyaDurumuWorkflowPanel";

const ASAM_RENK = {
    ON_TAHKIM: "text-sky-300",
    BASVURU: "text-cyan-300",
    HAKEM: "text-violet-300",
    BILIRKISI: "text-amber-300",
    ARA_KARAR: "text-emerald-300",
    KARAR: "text-[#D4AF37]",
    ODEME: "text-orange-300",
    KAPANDI: "text-slate-400",
};

export default function TahkimDosyaDetailSection({ row, onRefresh }) {
    const [sigorta, setSigorta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [kapanis, setKapanis] = useState(null);
    const [loglar, setLoglar] = useState([]);
    const [asamaSec, setAsamaSec] = useState(row?.tahkim_asamasi || "");
    const [gecisModal, setGecisModal] = useState(false);
    const sdId = row?.sigorta_dosyasi || row?.sigorta_dosyasi_id;

    const loadSigorta = useCallback(async () => {
        if (!sdId) return;
        setLoading(true);
        try {
            const data = await crud.retrieve("sigorta-dosyalari", sdId);
            setSigorta(data);
        } catch {
            setSigorta(null);
        } finally {
            setLoading(false);
        }
    }, [sdId]);

    const loadEk = useCallback(async () => {
        if (!row?.id) return;
        try {
            const [k, l] = await Promise.all([
                tahkimMerkez.kapanisGet(row.id),
                tahkimMerkez.islemLoglari(row.id),
            ]);
            setKapanis(k);
            setLoglar(l.loglar || []);
        } catch {
            setKapanis(null);
            setLoglar([]);
        }
    }, [row?.id]);

    useEffect(() => {
        loadSigorta();
        loadEk();
        setAsamaSec(row?.tahkim_asamasi || "");
    }, [loadSigorta, loadEk, row?.tahkim_asamasi]);

    const surec = Array.isArray(row?.surec_adimlari) ? row.surec_adimlari : [];
    const asamClass = ASAM_RENK[row?.tahkim_asamasi] || "text-slate-300";

    const asamaKaydet = () => {
        if (asamaSec === row?.tahkim_asamasi) {
            toast.message("Aşama değişmedi");
            return;
        }
        setGecisModal(true);
    };

    const altDurumSenkron = async () => {
        try {
            await tahkimMerkez.dosyaDurumdanAsama(row.id);
            toast.success("Sigorta alt durumdan aşama güncellendi");
            onRefresh?.();
            loadEk();
        } catch {
            toast.error("Senkron başarısız");
        }
    };

    const kapanisKaydet = async (kapat = false) => {
        try {
            const r = await tahkimMerkez.kapanisKontrol(row.id, {
                kapanis_evrak_tamam: kapanis?.kapanis_evrak_tamam,
                kapanis_odeme_onay: kapanis?.kapanis_odeme_onay,
                odeme_durumu: kapanis?.odeme_durumu,
                kapat,
            });
            setKapanis(r);
            toast.success(kapat ? "Dosya kapatıldı" : "Kapanış bilgileri kaydedildi");
            onRefresh?.();
            loadEk();
        } catch {
            toast.error("Kayıt başarısız");
        }
    };

    return (
        <div className="space-y-6 mb-8">
            <section className="rounded-xl border border-violet-500/30 bg-gradient-to-br from-[#121A2F] to-[#0B0F19] p-5">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <Scale className="h-5 w-5 text-violet-300" />
                    <h2 className="font-display text-lg text-white">Tahkim süreci</h2>
                    {row?.tahkim_asamasi_display && (
                        <span
                            className={`text-xs font-semibold px-2 py-0.5 rounded border border-white/10 ${asamClass}`}
                        >
                            {row.tahkim_asamasi_display}
                        </span>
                    )}
                    {row?.son_odeme_gecikme && (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-red-500/20 text-red-300">
                            Ödeme gecikmiş
                        </span>
                    )}
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                    <div className="rounded-lg bg-[#0B0F19]/80 p-3 border border-white/5">
                        <div className="text-slate-500 uppercase tracking-wider text-[10px]">Dosya no</div>
                        <div className="font-mono text-[#F3E5AB] mt-1">{row?.dosya_no || "—"}</div>
                    </div>
                    <div className="rounded-lg bg-[#0B0F19]/80 p-3 border border-white/5">
                        <div className="text-slate-500 uppercase tracking-wider text-[10px]">Başvuru no</div>
                        <div className="text-slate-200 mt-1">{row?.basvuru_no || "—"}</div>
                    </div>
                    <div className="rounded-lg bg-[#0B0F19]/80 p-3 border border-white/5">
                        <div className="text-slate-500 uppercase tracking-wider text-[10px]">Hakem</div>
                        <div className="text-slate-200 mt-1">{row?.hakem || "—"}</div>
                        {row?.hakem_email && (
                            <div className="text-[10px] text-slate-500 mt-0.5">{row.hakem_email}</div>
                        )}
                    </div>
                    <div className="rounded-lg bg-[#0B0F19]/80 p-3 border border-white/5">
                        <div className="text-slate-500 uppercase tracking-wider text-[10px]">Bilirkişi</div>
                        <div className="text-slate-200 mt-1">
                            {row?.bilirkisi_adi || "—"}
                            {row?.bilirkişi_ucreti != null && (
                                <span className="text-slate-500 ml-1">
                                    ({row.bilirkişi_ucreti} TL)
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="rounded-lg bg-[#0B0F19]/80 p-3 border border-white/5">
                        <div className="text-slate-500 uppercase tracking-wider text-[10px]">Son ödeme</div>
                        <div className="text-slate-200 mt-1">
                            {row?.son_odeme_tarihi || "—"}
                            {row?.gun_son_odeme != null && (
                                <span className="text-slate-500 ml-1">({row.gun_son_odeme} gün)</span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2 items-center">
                    <select
                        value={asamaSec}
                        onChange={(e) => setAsamaSec(e.target.value)}
                        className="rounded-lg bg-[#0B0F19] border border-white/10 text-sm px-2 py-1.5 text-white"
                    >
                        {TAHKIM_ASAMALARI.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={asamaKaydet}
                        className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-semibold"
                    >
                        Aşamayı değiştir
                    </button>
                    <button
                        type="button"
                        onClick={altDurumSenkron}
                        className="px-3 py-1.5 rounded-lg border border-white/10 text-xs text-slate-300"
                    >
                        Alt durumdan çek
                    </button>
                    <Link
                        to="/yeni/tahkim-merkezi"
                        className="text-xs text-violet-300 hover:underline ml-2"
                    >
                        Tahkim Merkezi →
                    </Link>
                    <Link
                        to="/e/tahkim-dosyalari"
                        className="text-xs text-[#D4AF37] hover:underline"
                    >
                        Veri girişi listesi →
                    </Link>
                </div>
                {sdId && (
                    <Link
                        to={`/e/sigorta-dosyalari/${sdId}`}
                        className="inline-flex items-center gap-1 mt-4 text-sm text-[#D4AF37] hover:underline"
                    >
                        Sigorta dosyasını aç <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                )}
            </section>

            <section className="rounded-xl border border-orange-500/20 bg-[#121A2F]/80 p-5">
                <div className="flex items-center gap-2 mb-3">
                    <Wallet className="h-4 w-4 text-orange-300" />
                    <h3 className="text-sm font-semibold text-white">Ödeme takibi</h3>
                </div>
                <div className="flex flex-wrap gap-3 text-xs items-center">
                    <span className="text-slate-400">
                        Durum:{" "}
                        <strong className="text-slate-200">
                            {row?.odeme_durumu_display || row?.odeme_durumu || "—"}
                        </strong>
                    </span>
                    {row?.bilirkişi_ucreti != null && (
                        <span className="text-slate-400">
                            Bilirkişi: <strong className="text-slate-200">{row.bilirkişi_ucreti} TL</strong>
                        </span>
                    )}
                </div>
            </section>

            {kapanis && (
                <section className="rounded-xl border border-emerald-500/20 bg-[#121A2F]/80 p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                        <h3 className="text-sm font-semibold text-white">Kapanış kontrol listesi</h3>
                        {kapanis.hazir && (
                            <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-200">
                                Kapatmaya hazır
                            </span>
                        )}
                    </div>
                    {!kapanis.hazir && kapanis.eksik?.length > 0 && (
                        <ul className="text-xs text-amber-200/90 mb-3 list-disc pl-4">
                            {kapanis.eksik.map((e) => (
                                <li key={e}>{e}</li>
                            ))}
                        </ul>
                    )}
                    <div className="flex flex-wrap gap-4 text-sm">
                        <label className="flex items-center gap-2 text-slate-300">
                            <input
                                type="checkbox"
                                checked={!!kapanis.kapanis_evrak_tamam}
                                onChange={(e) =>
                                    setKapanis((k) => ({
                                        ...k,
                                        kapanis_evrak_tamam: e.target.checked,
                                    }))
                                }
                            />
                            Evrak tamam
                        </label>
                        <label className="flex items-center gap-2 text-slate-300">
                            <input
                                type="checkbox"
                                checked={!!kapanis.kapanis_odeme_onay}
                                onChange={(e) =>
                                    setKapanis((k) => ({
                                        ...k,
                                        kapanis_odeme_onay: e.target.checked,
                                    }))
                                }
                            />
                            Ödeme onay
                        </label>
                        <select
                            value={kapanis.odeme_durumu || "BEKLIYOR"}
                            onChange={(e) =>
                                setKapanis((k) => ({ ...k, odeme_durumu: e.target.value }))
                            }
                            className="rounded bg-[#0B0F19] border border-white/10 text-xs px-2 py-1 text-white"
                        >
                            {ODEME_DURUMLARI.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="mt-3 flex gap-2">
                        <button
                            type="button"
                            onClick={() => kapanisKaydet(false)}
                            className="px-3 py-1.5 rounded border border-white/10 text-xs text-slate-200"
                        >
                            Kaydet
                        </button>
                        <button
                            type="button"
                            onClick={() => kapanisKaydet(true)}
                            disabled={!kapanis.hazir}
                            className="px-3 py-1.5 rounded bg-emerald-700 text-white text-xs font-semibold disabled:opacity-40"
                        >
                            Dosyayı kapat
                        </button>
                    </div>
                </section>
            )}

            {loading ? (
                <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-violet-400" />
                </div>
            ) : sigorta ? (
                <DosyaDurumuWorkflowPanel
                    dosyaId={Number(sdId)}
                    dosyaTipi={sigorta.dosya_tipi || "TAHKIM"}
                    currentDurum={sigorta.dosya_durumu || "ON_TAHKIM"}
                    dosyaDurumuLabel={sigorta.dosya_durumu_display || sigorta.dosya_durumu}
                    gun={0}
                    onTransition={() => {
                        loadSigorta();
                        onRefresh?.();
                    }}
                />
            ) : null}

            {surec.length > 0 && (
                <section className="rounded-xl border border-white/10 bg-[#121A2F] p-5">
                    <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                        <Clock className="h-4 w-4" /> Süreç zaman çizelgesi
                    </h3>
                    <div className="relative pl-4 border-l border-violet-500/30 space-y-4">
                        {surec.map((adim, i) => (
                            <div key={i} className="relative">
                                <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-violet-500 ring-4 ring-[#121A2F]" />
                                <div className="text-sm text-slate-200 font-medium">{adim.adim}</div>
                                {adim.tarih && (
                                    <div className="text-[10px] text-slate-500">{adim.tarih}</div>
                                )}
                                {adim.aciklama && (
                                    <div className="text-xs text-slate-400 mt-0.5">{adim.aciklama}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {loglar.length > 0 && (
                <section className="rounded-xl border border-white/10 bg-[#121A2F] p-4">
                    <h3 className="text-xs uppercase text-slate-500 mb-2 flex items-center gap-1">
                        <History className="h-3.5 w-3.5" /> İşlem geçmişi
                    </h3>
                    <ul className="space-y-1 max-h-40 overflow-y-auto text-xs">
                        {loglar.map((lg) => (
                            <li key={lg.id} className="text-slate-400 border-b border-white/5 py-1">
                                <span className="text-slate-300">{lg.alan}</span>: {lg.eski} → {lg.yeni}
                                {lg.kullanici && (
                                    <span className="text-slate-600"> · {lg.kullanici}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {row?.panel_notlar && (
                <section className="rounded-xl border border-white/10 bg-[#121A2F] p-4">
                    <h3 className="text-xs uppercase text-slate-500 mb-2">Tahkim notları</h3>
                    <p className="text-sm text-slate-300 whitespace-pre-wrap">{row.panel_notlar}</p>
                </section>
            )}

            <TahkimAsamaGecisModal
                open={gecisModal}
                dosya={{ ...row, tahkim_asamasi: row?.tahkim_asamasi }}
                hedefAsama={asamaSec}
                onClose={() => setGecisModal(false)}
                onSuccess={() => {
                    toast.success("Aşama kaydedildi");
                    onRefresh?.();
                    loadEk();
                }}
            />
        </div>
    );
}
